import Phaser from 'phaser';
import { viewportCss, ancoraCameraAlloSchermo } from '../renderScale';
import {
    LB_MAX_ROWS, LB_TOGGLE_EVENT, hudMetrics, hudBelowLeaderboard,
    loadLeaderboardCollapsed, saveLeaderboardCollapsed
} from '../hudLayout';
import { formatSolLabel } from '../format';

const KF_LIFETIME = 6000;  // ms prima che un chip della kill feed svanisca

const isTouch = () => 'ontouchstart' in window || navigator.maxTouchPoints > 0;

export default class UIScene extends Phaser.Scene {
    constructor() {
        super({ key: 'UIScene', active: false });
        this.killFeedEntries = [];
    }

    create() {
        // La HUD e' scritta tutta in coordinate di schermo (pixel CSS), e cosi'
        // resta: la camera fa la conversione verso i pixel fisici del canvas.
        // Vedi game/renderScale.js. Senza questo, su Retina la leaderboard
        // sarebbe grande la meta' e la soglia del layout compatto crederebbe di
        // stare su un monitor largo il doppio.
        ancoraCameraAlloSchermo(this.cameras.main);
        const { w: gameW, h: gameH } = viewportCss(this.scale);
        this.m = hudMetrics(gameW, gameH);
        this.touch = isTouch();

        // --- MINIMAP ---
        // Su schermi piccoli finisce in alto a destra: in basso a destra ci sta
        // il pulsante BOOST e il pollice coprirebbe il radar.
        this.minimapContainer = this.add.container(0, 0).setDepth(100);
        this.minimapRect = this.add.circle(0, 0, 1, 0x000000, 0.6);
        this.minimapRect.setStrokeStyle(2, 0x39FF14);
        this.minimapPlayerDot = this.add.circle(0, 0, 3, 0xFFFFFF);
        this.minimapRedZone = this.add.graphics();
        // Blip dei nemici: disegnati solo mentre il power-up RADAR e' attivo.
        this.minimapBlips = this.add.graphics();
        this.minimapLabel = this.add.text(0, 0, 'RADAR', {
            fontFamily: 'Courier New', fontSize: '10px', color: '#39FF14'
        });
        this.minimapContainer.add([
            this.minimapRect, this.minimapRedZone, this.minimapBlips,
            this.minimapPlayerDot, this.minimapLabel
        ]);

        // Sweep che gira mentre il radar e' attivo: segnala a colpo d'occhio
        // che i blip sono live e non un residuo dell'ultimo frame.
        this.minimapSweep = this.add.graphics();
        this.minimapSweep.setVisible(false);
        this.minimapContainer.addAt(this.minimapSweep, 2);
        this.radarBlips = null;

        // Faro del nucleo di frenesia: sta sopra a tutto il resto della
        // minimappa perche' e' l'unica cosa, li' dentro, per cui vale la pena
        // attraversare mezza arena.
        this.frenzyBeacon = this.add.graphics();
        this.frenzyBeacon.setVisible(false);
        this.minimapContainer.add(this.frenzyBeacon);
        this.frenzyBeaconPos = null;

        // --- LEADERBOARD (Top Left, pannello Vexel a tendina) ---
        this.lbCollapsed = loadLeaderboardCollapsed();
        this.leaderboardContainer = this.add.container(0, 0).setDepth(100);

        this.lbBg = this.add.graphics();

        // Titolo centrato nell'header, font Vexel (Space Mono), lettere spaziate e glow.
        this.lbTitle = this.add.text(0, 0, 'LEADERBOARD', {
            fontFamily: '"Space Mono", monospace', color: '#39FF14', fontStyle: 'bold'
        }).setOrigin(0.5, 0.5);
        this.lbTitle.setLetterSpacing(3);
        this.lbTitle.setShadow(0, 0, '#39FF14', 12);

        // Freccia: indica che l'header e' una maniglia apri/chiudi.
        this.lbChevron = this.add.text(0, 0, '▾', {
            fontFamily: '"Space Mono", monospace', color: '#39FF14', fontStyle: 'bold'
        }).setOrigin(1, 0.5);

        this.leaderboardContainer.add([this.lbBg, this.lbTitle, this.lbChevron]);

        // Una coppia di testi per riga: nome a sinistra, punteggio allineato a destra.
        this.leaderboardRows = [];
        for (let i = 0; i < LB_MAX_ROWS; i++) {
            const nameText = this.add.text(0, 0, `${i + 1}. ---`, {
                fontFamily: 'Courier New', color: '#4f8f4f'
            });
            const scoreText = this.add.text(0, 0, '', {
                fontFamily: 'Courier New', color: '#4f8f4f'
            }).setOrigin(1, 0);
            this.leaderboardRows.push({ nameText, scoreText });
            this.leaderboardContainer.add([nameText, scoreText]);
        }

        // Solo l'header e' cliccabile: il resto del pannello non ruba input al gioco.
        this.lbHeaderZone = this.add.zone(0, 0, 10, 10)
            .setOrigin(0, 0)
            .setInteractive({ useHandCursor: true });
        this.lbHeaderZone.on('pointerdown', () => this.toggleLeaderboard());
        this.lbHeaderZone.on('pointerover', () => this.lbChevron.setColor('#FFFFFF'));
        this.lbHeaderZone.on('pointerout', () => this.lbChevron.setColor('#39FF14'));
        this.leaderboardContainer.add(this.lbHeaderZone);

        // --- HUD POWERUP: countdown + bag (sotto la leaderboard) ---
        // Vivono qui, nella UIScene senza zoom, cosi' col Vision restano ancorati
        // al bordo dello schermo invece di rimpicciolirsi verso il centro.
        this.powerupTimerText = this.add.text(0, 0, '', {
            fontFamily: '"Space Mono", monospace', color: '#FFFFFF',
            stroke: '#000000', strokeThickness: 4, fontStyle: 'bold'
        }).setDepth(100).setVisible(false);

        // La "tasca": pannello Vexel (verde toxic) con testo Space Mono luminoso.
        this.bagContainer = this.add.container(0, 0).setDepth(100).setVisible(false);
        this.bagPanel = this.add.graphics();
        this.bagText = this.add.text(14, 9, '', {
            fontFamily: '"Space Mono", monospace', color: '#39FF14', fontStyle: 'bold'
        });
        this.bagText.setShadow(0, 0, '#39FF14', 12);
        this.bagContainer.add([this.bagPanel, this.bagText]);

        // Su touch la tasca e' anche il pulsante per usare il power-up: [E] non esiste.
        if (this.touch) {
            this.bagHitZone = this.add.zone(0, 0, 10, 10).setOrigin(0, 0).setInteractive();
            this.bagHitZone.on('pointerdown', () => {
                window.dispatchEvent(new Event('custom_powerup'));
            });
            this.bagContainer.add(this.bagHitZone);
        }

        // Effetto "respiro": la tasca pulsa piano per invitare a usarla.
        this.tweens.add({
            targets: this.bagContainer,
            alpha: { from: 1, to: 0.62 },
            duration: 850, yoyo: true, repeat: -1, ease: 'Sine.easeInOut'
        });

        // --- FRENZY: countdown in alto e vignetta ---------------------------
        // Il minuto di frenesia e' un evento della stanza, non un buff privato:
        // il countdown sta al centro in alto, dove lo vedono tutti, e la
        // vignetta dice "l'arena e' cambiata" senza rubare spazio al gioco.
        this.frenzyVignette = this.add.graphics().setDepth(98).setVisible(false);
        this.frenzyBanner = this.add.container(0, 0).setDepth(101).setVisible(false);
        this.frenzyPanel = this.add.graphics();
        this.frenzyTitle = this.add.text(0, 0, 'FRENZY', {
            fontFamily: '"Space Mono", monospace', color: '#FFFFFF', fontStyle: 'bold'
        }).setOrigin(0.5, 0);
        this.frenzyTitle.setShadow(0, 0, '#FFFFFF', 14);
        this.frenzyCountdown = this.add.text(0, 0, '', {
            fontFamily: '"Space Mono", monospace', color: '#FFFFFF', fontStyle: 'bold'
        }).setOrigin(0.5, 0);
        this.frenzyWho = this.add.text(0, 0, '', {
            fontFamily: '"Space Mono", monospace', color: '#9D00FF'
        }).setOrigin(0.5, 0);
        this.frenzyBanner.add([this.frenzyPanel, this.frenzyTitle, this.frenzyCountdown, this.frenzyWho]);

        // --- KILL FEED ---
        // Ogni evento e' un "chip" in stile Vexel (pannello scuro + bordo al neon).
        // Da desktop si impila in basso a sinistra; su mobile quell'angolo e' del
        // joystick, quindi passa in alto al centro e scende.
        this.killFeedEntries = [];
        this.killFeedContainer = this.add.container(0, 0).setDepth(100);

        // --- CASH OUT BUTTON (Bottom Center) ---
        // Pannello in stile Vexel: angoli tagliati, fondo scuro traslucido e
        // bordo oro al neon, come gli altri pannelli della HUD.
        this.cashoutContainer = this.add.container(0, 0).setDepth(100);
        this.cashoutPanel = this.add.graphics();

        this.cashoutText = this.add.text(0, 0, this.touch ? 'CASH OUT' : 'CASH OUT [C]', {
            fontFamily: '"Space Mono", monospace', color: '#FFD700', fontStyle: 'bold'
        }).setOrigin(0.5);
        this.cashoutText.setLetterSpacing(2);
        this.cashoutText.setShadow(0, 0, '#FFD700', 12);

        this.cashoutHitZone = this.add.zone(0, 0, 10, 10).setInteractive({ useHandCursor: true });
        this.cashoutContainer.add([this.cashoutPanel, this.cashoutText, this.cashoutHitZone]);

        this.cashoutHover = false;

        this.cashoutHitZone.on('pointerdown', () => {
            // Dispatch a custom event that MainScene listens to
            window.dispatchEvent(new Event('custom_cashout'));
        });

        // Hover effect
        this.cashoutHitZone.on('pointerover', () => {
            this.cashoutHover = true;
            this.drawCashoutButton();
        });
        this.cashoutHitZone.on('pointerout', () => {
            this.cashoutHover = false;
            this.drawCashoutButton();
        });

        // Pulsazione del bordo: il bottone che porta a casa i soldi non deve
        // sparire nel fondo scuro dell'arena.
        this.tweens.add({
            targets: this.cashoutContainer,
            alpha: { from: 0.88, to: 1 },
            duration: 1200, yoyo: true, repeat: -1, ease: 'Sine.easeInOut'
        });

        this.applyLayout(gameW, gameH);

        // Resize: su mobile succede in continuazione (rotazione, barra URL).
        this.handleResize = (gameSize) => {
            if (!this.sys.isActive()) return;
            this.cameras.main.setViewport(0, 0, gameSize.width, gameSize.height);
            // La viewport della camera e' in pixel fisici, la disposizione della
            // HUD in pixel CSS: l'aggancio va rifatto a ogni ridimensionamento,
            // perche' la correzione dipende dalla larghezza della camera.
            ancoraCameraAlloSchermo(this.cameras.main);
            const { w, h } = viewportCss(this.scale);
            this.applyLayout(w, h);
        };
        this.scale.on('resize', this.handleResize);

        // Ascoltiamo gli eventi da MainScene
        const mainScene = this.scene.get('MainScene');
        mainScene.events.on('update_minimap', this.updateMinimap, this);
        mainScene.events.on('update_stats', this.updateStats, this);
        mainScene.events.on('action_burn', this.showActionBurn, this);
        mainScene.events.on('kill_feed_event', this.addDistressSignal, this);
        mainScene.events.on('update_powerup_hud', this.updatePowerupHud, this);
        mainScene.events.on('update_radar', this.updateRadar, this);
        mainScene.events.on('update_frenzy', this.updateFrenzy, this);
        mainScene.events.on('update_frenzy_beacon', this.updateFrenzyBeacon, this);

        // Toggle della leaderboard da tastiera (tasto L, gestito in MainScene).
        this.handleLbToggleRequest = () => this.toggleLeaderboard();
        this.game.events.on('request_lb_toggle', this.handleLbToggleRequest);

        // Pulizia se chiude o si distrugge
        this.events.on('shutdown', () => {
            this.scale.off('resize', this.handleResize);
            this.game.events.off('request_lb_toggle', this.handleLbToggleRequest);
            mainScene.events.off('update_minimap', this.updateMinimap, this);
            mainScene.events.off('update_stats', this.updateStats, this);
            mainScene.events.off('action_burn', this.showActionBurn, this);
            mainScene.events.off('kill_feed_event', this.addDistressSignal, this);
            mainScene.events.off('update_powerup_hud', this.updatePowerupHud, this);
            mainScene.events.off('update_radar', this.updateRadar, this);
            mainScene.events.off('update_frenzy', this.updateFrenzy, this);
            mainScene.events.off('update_frenzy_beacon', this.updateFrenzyBeacon, this);
        });
    }

    // Riposiziona e ridimensiona tutta la HUD per la viewport corrente.
    applyLayout(w, h) {
        const m = hudMetrics(w, h);
        this.m = m;

        // Minimap: in alto a destra su compatto (sotto la rotellina), in basso a
        // destra altrimenti.
        const r = m.minimapSize / 2;
        const mmX = w - r - m.margin;
        const mmY = m.compact ? m.margin + 52 + r : h - r - m.margin;
        this.minimapContainer.setPosition(mmX, mmY);
        this.minimapRect.setRadius(r);
        this.minimapLabel.setPosition(-r + 5, -r + 5);
        this.minimapLabel.setFontSize(m.compact ? 8 : 10);

        // Leaderboard
        this.leaderboardContainer.setPosition(m.margin, m.margin);
        const headerMid = (m.lbHeaderH - 8) / 2;
        this.lbTitle.setPosition(m.lbWidth / 2, headerMid);
        this.lbTitle.setFontSize(m.lbTitleSize);
        this.lbTitle.setLetterSpacing(m.compact ? 2 : 3);
        this.lbChevron.setPosition(m.lbWidth - m.lbPad, headerMid);
        this.lbChevron.setFontSize(m.compact ? 11 : 13);

        this.leaderboardRows.forEach((row, i) => {
            const y = m.lbHeaderH + i * m.lbRowH;
            row.nameText.setPosition(m.lbPad, y);
            row.scoreText.setPosition(m.lbWidth - m.lbPad, y);
            row.nameText.setFontSize(m.lbRowSize);
            row.scoreText.setFontSize(m.lbRowSize);
        });

        // La maniglia deve restare comoda da toccare anche quando l'header e' basso.
        this.lbHeaderZone.setSize(m.lbWidth, Math.max(m.lbHeaderH, this.touch ? 40 : m.lbHeaderH));

        this.drawLeaderboardPanel();

        // HUD power-up
        this.powerupTimerText.setFontSize(m.timerFontSize);
        this.powerupTimerText.setX(m.margin);
        this.bagText.setFontSize(m.bagFontSize);
        this.repositionPowerupHud();
        this.redrawBagPanel();

        // Frenzy: banner al centro in alto, vignetta su tutto lo schermo.
        this.frenzyTitle.setFontSize(m.compact ? 12 : 15);
        this.frenzyCountdown.setFontSize(m.compact ? 20 : 26);
        this.frenzyWho.setFontSize(m.compact ? 9 : 11);
        this.frenzyBanner.setPosition(w / 2, m.margin);
        this.disegnaBannerFrenzy();
        this.disegnaVignettaFrenzy(w, h);

        // Cash out
        const cx = w / 2;
        const cy = h - m.margin - m.cashoutH / 2;
        this.cashoutContainer.setPosition(cx, cy);
        this.cashoutText.setFontSize(m.cashoutFontSize);
        this.cashoutHitZone.setSize(m.cashoutW, m.cashoutH);
        // setSize non tocca l'area di input: senza questo il tap resterebbe
        // tarato sulla misura vecchia.
        if (this.cashoutHitZone.input && this.cashoutHitZone.input.hitArea) {
            this.cashoutHitZone.input.hitArea.setTo(0, 0, m.cashoutW, m.cashoutH);
        }
        this.drawCashoutButton();

        // Kill feed
        if (m.compact) {
            this.killFeedContainer.setPosition(w / 2, m.margin);
        } else {
            this.killFeedContainer.setPosition(m.margin, h - m.margin);
        }
        while (this.killFeedEntries.length > m.kfMax) {
            this.removeKillFeedEntry(this.killFeedEntries[0]);
        }
        this.layoutKillFeed(true);
    }

    // Bottone CASH OUT: pannello con gli angoli tagliati in diagonale (la forma
    // "tech" dei pannelli Vexel), disegnato attorno all'origine del container.
    drawCashoutButton() {
        if (!this.cashoutPanel) return;
        const m = this.m;
        const w = m.cashoutW;
        const h = m.cashoutH;
        const cut = Math.round(h * 0.32); // taglio degli angoli
        const x = -w / 2;
        const y = -h / 2;

        // Solo due angoli tagliati (in alto a sinistra e in basso a destra):
        // asimmetria voluta, e' la firma dei pannelli del brand.
        const shape = [
            x + cut, y,
            x + w, y,
            x + w, y + h - cut,
            x + w - cut, y + h,
            x, y + h,
            x, y + cut,
        ];

        const hover = this.cashoutHover;
        const g = this.cashoutPanel;
        g.clear();

        // Alone esterno: due contorni sempre piu' larghi e trasparenti.
        g.lineStyle(hover ? 9 : 7, 0xFFD700, hover ? 0.18 : 0.1);
        g.strokePoints(this.pointsFromFlat(shape), true, true);

        g.fillStyle(0x120d00, hover ? 0.92 : 0.8);
        g.fillPoints(this.pointsFromFlat(shape), true);

        // Riempimento oro tenue in hover: il bottone "si carica" al passaggio.
        if (hover) {
            g.fillStyle(0xFFD700, 0.16);
            g.fillPoints(this.pointsFromFlat(shape), true);
        }

        g.lineStyle(2, 0xFFD700, hover ? 1 : 0.75);
        g.strokePoints(this.pointsFromFlat(shape), true, true);

        // Barretta laterale piena, come nei chip della kill feed.
        g.fillStyle(0xFFD700, hover ? 1 : 0.8);
        g.fillRect(x + 6, y + cut * 0.6, 4, h - cut * 1.2);

        this.cashoutText.setColor(hover ? '#FFF3B0' : '#FFD700');
        this.cashoutText.setShadow(0, 0, '#FFD700', hover ? 20 : 12);
    }

    pointsFromFlat(flat) {
        const pts = [];
        for (let i = 0; i < flat.length; i += 2) {
            pts.push(new Phaser.Math.Vector2(flat[i], flat[i + 1]));
        }
        return pts;
    }

    updateMinimap(pctX, pctY, redZoneRadius, mapSize) {
        if (!this.minimapPlayerDot || !this.minimapRect) return;

        const maxRadius = this.minimapRect.radius || 75;
        const minimapDiameter = maxRadius * 2;

        const dx = (pctX - 0.5) * minimapDiameter;
        const dy = (pctY - 0.5) * minimapDiameter;

        const dist = Math.sqrt(dx * dx + dy * dy);

        let finalX = dx;
        let finalY = dy;

        // Limita il pallino all'interno del cerchio
        if (dist > maxRadius) {
            finalX = (dx / dist) * maxRadius;
            finalY = (dy / dist) * maxRadius;
        }

        this.minimapPlayerDot.setPosition(finalX, finalY);

        if (this.minimapRedZone) {
            this.minimapRedZone.clear(); // Non disegniamo più il cerchio rosso interno
        }
    }

    // RADAR (power-up): blips = [{x, y}] in coordinate 0-1 sulla mappa, oppure
    // null quando l'effetto scade.
    // Countdown della frenesia. `frenzy` e' null quando non e' in corso.
    updateFrenzy(frenzy) {
        const attiva = !!frenzy;
        if (this.frenzyBanner) this.frenzyBanner.setVisible(attiva);
        if (this.frenzyVignette) this.frenzyVignette.setVisible(attiva);
        if (!attiva) {
            if (this.frenzyPulse) { this.frenzyPulse.stop(); this.frenzyPulse = null; }
            // Il tween lascia i target dove li ha fermati: senza questo, alla
            // frenesia dopo il banner ricomparirebbe gia' mezzo trasparente.
            this.frenzyBanner.setAlpha(1);
            this.frenzyVignette.setAlpha(1);
            return;
        }

        const secondi = Math.max(0, frenzy.resta / 1000);
        this.frenzyCountdown.setText(secondi >= 10
            ? `0:${Math.ceil(secondi).toString().padStart(2, '0')}`
            : secondi.toFixed(1));
        this.frenzyWho.setText(frenzy.portatore ? `▲ ${frenzy.portatore}` : 'NESSUN PORTATORE');
        this.disegnaBannerFrenzy();

        // Il respiro parte una volta sola, non a ogni tick.
        if (!this.frenzyPulse) {
            this.frenzyPulse = this.tweens.add({
                targets: [this.frenzyBanner, this.frenzyVignette],
                alpha: { from: 1, to: 0.55 },
                duration: 420, yoyo: true, repeat: -1, ease: 'Sine.easeInOut'
            });
        }
    }

    disegnaBannerFrenzy() {
        if (!this.frenzyPanel) return;
        const larghezza = Math.max(150, this.frenzyCountdown.width + 80);
        const altezza = 74;
        this.frenzyTitle.setY(8);
        this.frenzyCountdown.setY(24);
        this.frenzyWho.setY(altezza - 18);

        this.frenzyPanel.clear();
        this.frenzyPanel.fillStyle(0x08060C, 0.82);
        this.frenzyPanel.fillRoundedRect(-larghezza / 2, 0, larghezza, altezza, 10);
        this.frenzyPanel.lineStyle(2, 0xFFFFFF, 0.9);
        this.frenzyPanel.strokeRoundedRect(-larghezza / 2, 0, larghezza, altezza, 10);
    }

    // La cornice luminosa che dice "l'arena e' in frenesia". Solo un bordo:
    // un velo su tutto lo schermo darebbe fastidio per un minuto intero.
    disegnaVignettaFrenzy(w, h) {
        if (!this.frenzyVignette) return;
        this.frenzyVignette.clear();
        for (let i = 0; i < 5; i++) {
            this.frenzyVignette.lineStyle(10, 0xFFFFFF, 0.05 - i * 0.008);
            this.frenzyVignette.strokeRect(i * 5, i * 5, w - i * 10, h - i * 10);
        }
        this.frenzyVignette.lineStyle(3, 0x9D00FF, 0.55);
        this.frenzyVignette.strokeRect(2, 2, w - 4, h - 4);
    }

    // Faro sulla minimappa: dove sta il nucleo, finche' nessuno lo raccoglie.
    updateFrenzyBeacon(pos) {
        this.frenzyBeaconPos = pos || null;
        if (!this.frenzyBeacon) return;
        if (!pos) {
            this.frenzyBeacon.clear();
            this.frenzyBeacon.setVisible(false);
            return;
        }
        const maxRadius = this.minimapRect ? (this.minimapRect.radius || 75) : 75;
        const dx = (pos.x - 0.5) * maxRadius * 2;
        const dy = (pos.y - 0.5) * maxRadius * 2;
        // Si pulisce PRIMA di decidere: se no, un nucleo finito fuori dal disco
        // lascerebbe acceso il puntino dove stava un attimo prima.
        this.frenzyBeacon.clear();
        if (Math.sqrt(dx * dx + dy * dy) > maxRadius - 2) {
            this.frenzyBeacon.setVisible(false);
            return;
        }

        this.frenzyBeacon.setVisible(true);
        this.frenzyBeacon.fillStyle(0xFFFFFF, 0.20);
        this.frenzyBeacon.fillCircle(dx, dy, 9);
        this.frenzyBeacon.fillStyle(0xFFFFFF, 1);
        this.frenzyBeacon.fillCircle(dx, dy, 3);

        if (!this.frenzyBeaconPulse) {
            this.frenzyBeaconPulse = this.tweens.add({
                targets: this.frenzyBeacon,
                alpha: { from: 1, to: 0.25 },
                duration: 500, yoyo: true, repeat: -1, ease: 'Sine.easeInOut'
            });
        }
    }

    updateRadar(blips) {
        this.radarBlips = blips && blips.length ? blips : null;

        if (!this.minimapBlips) return;

        if (!this.radarBlips) {
            this.minimapBlips.clear();
            if (this.minimapSweep) {
                this.minimapSweep.clear();
                this.minimapSweep.setVisible(false);
            }
            // Il bordo torna al verde standard della HUD.
            if (this.minimapRect) this.minimapRect.setStrokeStyle(2, 0x39FF14);
            return;
        }

        const maxRadius = this.minimapRect ? (this.minimapRect.radius || 75) : 75;
        const diameter = maxRadius * 2;

        // Bordo acceso: il radar e' un vantaggio a tempo, deve saltare all'occhio.
        if (this.minimapRect) this.minimapRect.setStrokeStyle(2, 0xFF3B3B);

        this.minimapBlips.clear();
        this.radarBlips.forEach((b) => {
            const dx = (b.x - 0.5) * diameter;
            const dy = (b.y - 0.5) * diameter;
            // Fuori dal disco della minimappa non c'e' mappa: nulla da segnare.
            if (Math.sqrt(dx * dx + dy * dy) > maxRadius - 2) return;

            this.minimapBlips.fillStyle(0xFF3B3B, 0.22);
            this.minimapBlips.fillCircle(dx, dy, 6);
            this.minimapBlips.fillStyle(0xFF3B3B, 1);
            this.minimapBlips.fillCircle(dx, dy, 2.5);
        });

        this.drawRadarSweep(maxRadius);
    }

    drawRadarSweep(maxRadius) {
        if (!this.minimapSweep) return;

        // Un giro ogni 2s, ricavato dal clock della scena: nessun tween da
        // avviare e fermare insieme all'effetto.
        const angle = (this.time.now / 2000) * Math.PI * 2;
        const spread = 0.45;

        this.minimapSweep.setVisible(true);
        this.minimapSweep.clear();
        this.minimapSweep.fillStyle(0xFF3B3B, 0.12);
        this.minimapSweep.slice(0, 0, maxRadius - 2, angle - spread, angle, false);
        this.minimapSweep.fillPath();
        this.minimapSweep.lineStyle(1, 0xFF3B3B, 0.5);
        this.minimapSweep.lineBetween(0, 0, Math.cos(angle) * (maxRadius - 2), Math.sin(angle) * (maxRadius - 2));
    }

    drawLeaderboardPanel() {
        const m = this.m;
        const h = this.lbCollapsed ? m.lbCollapsedHeight : m.lbHeight;

        this.lbBg.clear();
        this.lbBg.fillStyle(0x060c06, 0.55);
        this.lbBg.fillRoundedRect(0, 0, m.lbWidth, h, 10);
        this.lbBg.lineStyle(1, 0x39FF14, 0.35);
        this.lbBg.strokeRoundedRect(0, 0, m.lbWidth, h, 10);

        if (!this.lbCollapsed) {
            // Riga divisoria sotto il titolo
            this.lbBg.lineStyle(1, 0x39FF14, 0.18);
            this.lbBg.lineBetween(m.lbPad, m.lbHeaderH - 8, m.lbWidth - m.lbPad, m.lbHeaderH - 8);
        }

        this.lbChevron.setText(this.lbCollapsed ? '▸' : '▾');
        this.leaderboardRows.forEach((row, i) => {
            // Su schermo piccolo mostriamo solo la testa della classifica.
            const visible = !this.lbCollapsed && i < m.lbRows;
            row.nameText.setVisible(visible);
            row.scoreText.setVisible(visible);
        });
    }

    toggleLeaderboard() {
        this.lbCollapsed = !this.lbCollapsed;
        saveLeaderboardCollapsed(this.lbCollapsed);
        this.drawLeaderboardPanel();
        // Le scritte dei powerup risalgono/riscendono nello spazio della leaderboard.
        this.repositionPowerupHud();
        this.game.events.emit(LB_TOGGLE_EVENT, this.lbCollapsed);
    }

    repositionPowerupHud() {
        const y = hudBelowLeaderboard(this.lbCollapsed, this.m);
        if (this.powerupTimerText) this.powerupTimerText.setY(y);
        this.positionBag();
    }

    // Su desktop la tasca sta sotto la leaderboard. Su mobile quell'angolo e'
    // coperto dalla zona del joystick, che si mangerebbe il tap: la spostiamo
    // sopra il CASH OUT, nella fascia centrale libera tra i due pollici.
    positionBag() {
        if (!this.bagContainer) return;
        const m = this.m;

        if (m.compact) {
            const { w, h } = viewportCss(this.scale);
            const bagW = this.bagPanelWidth || 0;
            const bagH = this.bagPanelHeight || 44;
            this.bagContainer.setPosition(
                Math.round(w / 2 - bagW / 2),
                h - m.margin - m.cashoutH - 10 - bagH
            );
            return;
        }

        this.bagContainer.setPosition(
            m.margin,
            hudBelowLeaderboard(this.lbCollapsed, m) + 40
        );
    }

    updatePowerupHud(data) {
        if (!this.powerupTimerText) return;

        if (data && data.timer) {
            this.powerupTimerText.setText(data.timer.text);
            this.powerupTimerText.setColor(data.timer.color);
            this.powerupTimerText.setVisible(true);
        } else {
            this.powerupTimerText.setVisible(false);
        }

        this.setBag(data ? data.held : null);
    }

    setBag(held) {
        if (!this.bagContainer) return;

        if (!held) {
            this.heldPowerup = null;
            this.bagContainer.setVisible(false);
            return;
        }

        this.heldPowerup = held;
        // Su touch il suggerimento e' "TAP", non il tasto E.
        this.bagText.setText(`${held.icon || '🎒'} ${held.name}  ·  ${this.touch ? 'TAP' : '[E]'}`);
        this.bagText.setColor(held.color);
        this.bagText.setShadow(0, 0, held.color, 12);

        this.redrawBagPanel();
        this.bagContainer.setVisible(true);
    }

    redrawBagPanel() {
        if (!this.heldPowerup || !this.bagPanel) return;

        const colInt = parseInt(this.heldPowerup.color.replace('#', '0x'), 16);
        // Pannello ridimensionato sul testo, con bordo del colore del power-up.
        // Su touch garantiamo comunque un'area di tocco decente.
        const w = this.bagText.width + 28;
        const h = Math.max(this.bagText.height + 18, this.touch ? 44 : 0);
        this.bagText.setY((h - this.bagText.height) / 2);
        this.bagPanel.clear();
        this.bagPanel.fillStyle(0x060c06, 0.66);
        this.bagPanel.fillRoundedRect(0, 0, w, h, 10);
        this.bagPanel.lineStyle(1.5, colInt, 0.85);
        this.bagPanel.strokeRoundedRect(0, 0, w, h, 10);

        if (this.bagHitZone) this.bagHitZone.setSize(w, h);

        this.bagPanelWidth = w;
        this.bagPanelHeight = h;
        this.positionBag(); // la larghezza dipende dal nome del power-up
    }

    updateStats(leaderboardArr, myId) {
        if (!this.leaderboardRows || !leaderboardArr) return;
        if (this.lbCollapsed) return; // righe nascoste: inutile aggiornarle

        this.leaderboardRows.forEach((row, i) => {
            if (i >= this.m.lbRows) return; // riga non visibile in layout compatto
            const p = leaderboardArr[i];

            if (!p) {
                row.nameText.setText(`${i + 1}. ---`);
                row.nameText.setColor('#2f5f2f');
                row.nameText.setShadow(0, 0, '#000000', 0);
                row.scoreText.setText('');
                return;
            }

            const isMe = myId !== undefined && p.id === myId;
            const name = isMe ? 'YOU' : (p.name ? p.name.substring(0, this.m.compact ? 8 : 10) : 'UNKNOWN');

            row.nameText.setText(`${i + 1}. ${name}`);
            row.scoreText.setText(formatSolLabel(p.score));

            // Il giocatore risalta sempre; sopra di lui la classifica sfuma dal bianco al verde scuro.
            let color;
            if (isMe) color = '#39FF14';
            else if (i === 0) color = '#FFFFFF';
            else if (i < 3) color = '#9dffa0';
            else color = '#6f9f6f';

            row.nameText.setColor(color);
            row.scoreText.setColor(color);
            row.nameText.setShadow(0, 0, '#39FF14', isMe ? 8 : 0);
        });
    }

    showActionBurn(amount) {
        const { w: gameW, h: gameH } = viewportCss(this.scale);

        const floatText = this.add.text(gameW / 2, gameH / 2 - 50, `[-$${amount} BURNED] 🔥`, {
            fontFamily: 'Courier New', fontSize: this.m.compact ? '18px' : '24px',
            color: '#ff4444', fontStyle: 'bold'
        }).setOrigin(0.5).setDepth(200);

        this.tweens.add({
            targets: floatText,
            y: floatText.y - 100,
            alpha: 0,
            duration: 2500,
            ease: 'Power2',
            onComplete: () => {
                floatText.destroy();
            }
        });
    }

    addDistressSignal(data) {
        // data has: { victim, action, killer }
        // Dopo uno shutdown la scena puo' ricevere ancora eventi in coda: senza questo
        // controllo si finirebbe a scrivere su oggetti gia' distrutti.
        if (!this.sys.isActive() || !this.killFeedContainer || !this.killFeedContainer.list) return;

        const action = (data && data.action) || 'TERMINATED';
        const maxName = this.m.compact ? 9 : 12;
        const victimName = data && data.victim ? data.victim.substring(0, maxName) : 'UNKNOWN';
        const killerName = data && data.killer ? data.killer.substring(0, maxName) : 'SYSTEM';

        // Il feed non porta solo morti: l'estrazione riuscita e l'arrivo di un
        // nuovo operativo sono eventi buoni, e devono leggersi come tali invece
        // di comparire in rosso come uno schianto.
        const cashout = action === 'EXTRACTED SAFELY';
        const arrivo = action === 'ENTERED THE ARENA';

        let accent = '#ff4444';
        let accentInt = 0xff4444;
        let icon = '💀';
        if (cashout) { accent = '#FFD700'; accentInt = 0xFFD700; icon = '💰'; }
        else if (arrivo) { accent = '#00E5FF'; accentInt = 0x00E5FF; icon = '🛰'; }

        // Su questi due il "killer" e' sempre SYSTEM: stamparlo non aggiunge
        // niente e allunga il chip.
        const senzaKiller = cashout || arrivo;

        const entry = this.buildKillFeedChip({ icon, victimName, action, killerName, accent, accentInt, senzaKiller });
        this.killFeedContainer.add(entry.container);
        this.killFeedEntries.push(entry);

        // Oltre kfMax il piu' vecchio esce subito, per non invadere lo schermo.
        while (this.killFeedEntries.length > this.m.kfMax) {
            this.removeKillFeedEntry(this.killFeedEntries[0]);
        }

        entry.container.setAlpha(0);
        // Il nuovo chip lo posiziono io (parte sfalsato); gli altri scorrono animati.
        this.layoutKillFeed(false, entry);

        // Entrata in dissolvenza con un piccolo scarto: l'occhio la nota senza
        // che il movimento disturbi la partita.
        entry.container.x -= this.m.compact ? 0 : 24;
        entry.container.y -= this.m.compact ? 10 : 0;
        this.tweens.add({
            targets: entry.container,
            x: entry.targetX, y: entry.targetY, alpha: 1,
            duration: 220, ease: 'Cubic.easeOut'
        });

        entry.timer = this.time.delayedCall(KF_LIFETIME, () => this.removeKillFeedEntry(entry));
    }

    buildKillFeedChip({ icon, victimName, action, killerName, accent, accentInt, senzaKiller }) {
        const m = this.m;
        const container = this.add.container(0, 0);
        const bg = this.add.graphics();
        container.add(bg);

        const font = {
            fontFamily: '"Space Mono", monospace',
            fontSize: `${m.kfFontSize}px`,
            fontStyle: 'bold'
        };
        let x = m.kfPad;
        const y = m.kfHeight / 2;
        const gap = m.compact ? 5 : 7;

        const push = (text, color, glow) => {
            const t = this.add.text(x, y, text, { ...font, color }).setOrigin(0, 0.5);
            if (glow) t.setShadow(0, 0, glow, 10);
            container.add(t);
            x += t.width + gap;
            return t;
        };

        push(icon, '#FFFFFF');
        push(victimName, '#FFFFFF');
        push(action, accent, accent);
        if (!senzaKiller) push(killerName, '#39FF14', '#39FF14');

        const w = x - gap + m.kfPad;
        bg.fillStyle(0x060c06, 0.72);
        bg.fillRoundedRect(0, 0, w, m.kfHeight, 8);
        bg.lineStyle(1, accentInt, 0.5);
        bg.strokeRoundedRect(0, 0, w, m.kfHeight, 8);
        // Barretta laterale colorata: firma visiva dei pannelli Vexel.
        bg.fillStyle(accentInt, 0.9);
        bg.fillRoundedRect(0, 4, 3, m.kfHeight - 8, 2);

        return { container, width: w, timer: null, dying: false, targetX: 0, targetY: 0 };
    }

    removeKillFeedEntry(entry) {
        if (!entry || entry.dying) return;
        entry.dying = true;
        if (entry.timer) entry.timer.remove();

        const idx = this.killFeedEntries.indexOf(entry);
        if (idx !== -1) this.killFeedEntries.splice(idx, 1);

        this.tweens.add({
            targets: entry.container,
            alpha: 0, duration: 200, ease: 'Cubic.easeIn',
            onComplete: () => entry.container.destroy()
        });

        this.layoutKillFeed();
    }

    // snapAll = riposiziona tutto senza animare (resize della finestra).
    // snapEntry = il chip appena creato, che si posiziona da solo.
    layoutKillFeed(snapAll, snapEntry) {
        const m = this.m;
        const n = this.killFeedEntries.length;

        this.killFeedEntries.forEach((entry, i) => {
            if (m.compact) {
                // In alto al centro: chip centrati, il piu' recente in fondo alla pila.
                entry.targetX = -entry.width / 2;
                entry.targetY = i * m.kfStep;
            } else {
                // In basso a sinistra: il piu' recente appoggiato al margine.
                entry.targetX = 0;
                entry.targetY = -(n - i) * m.kfStep;
            }

            if (snapAll || entry === snapEntry) {
                entry.container.setPosition(entry.targetX, entry.targetY);
                return;
            }
            this.tweens.add({
                targets: entry.container,
                x: entry.targetX, y: entry.targetY,
                duration: 180, ease: 'Cubic.easeOut'
            });
        });
    }
}
